import { createUrl } from "@/redux/api";
import { BuilderType, PaginatedResponse } from "@/redux/types";
import {
  BaseDiscussionResponse,
  DiscussionQueryParams,
  DiscussionTags,
  FullDiscussionResponse,
  TagType,
} from "@/redux/discussions/types";
import { isPaginatedResponse } from "@/redux/typeGuards";

const discussionsService = (builder: BuilderType<DiscussionTags>) => ({
  paginateFilterDiscussions: builder.query<
    PaginatedResponse<BaseDiscussionResponse> | BaseDiscussionResponse[],
    DiscussionQueryParams
  >({
    query: (queryParams) => ({
      url: createUrl("/discussions/", queryParams),
      method: "GET",
    }),
    providesTags: (result) =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as BaseDiscussionResponse[]
            ).map((i): TagType => ({ type: "Discussion", id: i.id })),
            { type: "Discussions" },
          ]
        : [{ type: "Discussions" }],
  }),
  getDiscussionsStatistics: builder.query({
    query: (queryParams) => ({
      url: createUrl("/discussions/statistics/", queryParams),
      method: "GET",
    }),
    providesTags: (result) =>
      result ? [{ type: "DiscussionsStatistics" }] : [],
  }),
  getDiscussionById: builder.query<FullDiscussionResponse, number | string>({
    query: (id) => ({
      url: `/discussions/${id}/`,
      method: "GET",
    }),
    providesTags: (result) =>
      result ? [{ type: "Discussion", id: result.id }] : [],
  }),
  createDiscussion: builder.mutation<FullDiscussionResponse, any>({
    query: (data) => ({
      url: "/discussions/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            { type: "Discussions" },
            { type: "UserAnalytics" },
            ...result.attachedBlocks.map(
              (block): TagType => ({
                type: "DataBlockReference",
                id: block.id,
              }),
            ),
          ]
        : [],
  }),
  updateDiscussion: builder.mutation<
    FullDiscussionResponse,
    { id: number | string; data: any }
  >({
    query: ({ id, data }) => ({
      url: `/discussions/${id}/`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            { type: "Discussion", id: result.id },
            { type: "UserAnalytics" },
            ...result.attachedBlocks.map(
              (block): TagType => ({
                type: "DataBlockReference",
                id: block.id,
              }),
            ),
          ]
        : [],
  }),
  patchDiscussion: builder.mutation<
    FullDiscussionResponse,
    { id: number | string; data: any }
  >({
    query: ({ id, data }) => ({
      url: `/discussions/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            { type: "Discussion", id: result.id },
            { type: "UserAnalytics" },
            ...result.attachedBlocks.map(
              (block): TagType => ({
                type: "DataBlockReference",
                id: block.id,
              }),
            ),
          ]
        : [],
  }),
  likeDiscussion: builder.mutation({
    query: ({ id, isLiked }) => ({
      url: `/discussions/${id}/likes/`,
      method: "POST",
      data: { isLiked },
    }),
    invalidatesTags: (result) =>
      result ? [{ type: "Discussion", id: result.discussion }] : [],
  }),
  deleteDiscussion: builder.mutation<void, number | string>({
    query: (id) => ({
      url: `/discussions/${id}/`,
      method: "DELETE",
    }),
    invalidatesTags: (_, error) =>
      error
        ? []
        : [
            { type: "Discussions" },
            { type: "DiscussionsStatistics" },
            { type: "Topics" },
            { type: "UserAnalytics" },
          ],
  }),
  reportDiscussion: builder.query<null, number | string>({
    query: (id) => ({
      url: `/discussions/${id}/report/`,
      method: "GET",
    }),
    invalidatesTags: (_, error, args) =>
      error ? [] : [{ type: "Discussion", id: args }],
  }),
  incrementDiscussionViews: builder.query<null, number | string>({
    query: (id) => ({
      url: `/discussions/${id}/views/`,
      method: "GET",
    }),
    invalidatesTags: (result) =>
      result ? [{ type: "Discussion", id: result.id }] : [],
  }),
});

export default discussionsService;
